// =============================================================================
// Origin UI — src/services/offerApi.ts
// API service for Offer Management endpoints.
// Batch 1.5 (list) + 1.6 (detail/update/status) + 1.7 (documents).
// =============================================================================

import { api } from './apiService';

// ─── Types ───

export interface OfferListItem {
  offerId: string;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  departmentName: string;
  officeName: string;
  employmentType: string;
  proposedStartDate: string | null;
  status: string;
  visaRequired: boolean;
  visaStatus: string | null;
  createdAt: string;
  offerExpiryDate: string | null;
}

export interface OfferDocumentDto {
  offerDocumentId: string;
  offerId: string;
  fileName: string;
  blobPath: string;
  category: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface OfferDetail {
  offerId: string;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  departmentId: number;
  departmentName: string;
  officeLocationId: number;
  officeName: string;
  lineManagerId: string | null;
  lineManagerName: string | null;
  employmentType: string;
  proposedStartDate: string | null;
  proposedSalary: number | null;
  salaryCurrency: string | null;
  offerExpiryDate: string | null;
  status: string;
  contractTemplateId: string | null;
  generatedContractBlobPath: string | null;
  notes: string | null;
  visaRequired: boolean;
  visaType: string | null;
  visaStatus: string | null;
  visaNotes: string | null;
  onboardingRequestId: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  documents: OfferDocumentDto[];
}

export interface CreateOfferDto {
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  departmentId: number;
  officeLocationId: number;
  lineManagerId: string | null;
  employmentType: string;
  proposedStartDate: string | null;
  proposedSalary: number | null;
  salaryCurrency: string | null;
  offerExpiryDate: string | null;
  notes: string | null;
  visaRequired: boolean;
  visaType: string | null;
}

export interface UpdateOfferDto {
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  departmentId: number;
  officeLocationId: number;
  lineManagerId: string | null;
  employmentType: string;
  proposedStartDate: string | null;
  proposedSalary: number | null;
  salaryCurrency: string | null;
  offerExpiryDate: string | null;
  notes: string | null;
  visaRequired: boolean;
  visaType: string | null;
  visaStatus: string | null;
  visaNotes: string | null;
}

// Document categories for offers
export const OFFER_DOC_CATEGORIES = [
  'CV',
  'SignedOffer',
  'Passport',
  'VisaDocument',
  'Certificate',
  'Medical',
  'Other',
] as const;

export type OfferDocCategory = typeof OFFER_DOC_CATEGORIES[number];

// ─── API ───

export const offerApi = {
  // List
  getOffers: (params?: { status?: string; officeLocationId?: number; departmentId?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.officeLocationId) query.set('officeLocationId', params.officeLocationId.toString());
    if (params?.departmentId) query.set('departmentId', params.departmentId.toString());
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return api.get<OfferListItem[]>(`/api/offers${qs ? `?${qs}` : ''}`);
  },

  // Detail
  getOffer: (id: string) =>
    api.get<OfferDetail>(`/api/offers/${id}`),

  // Create
  createOffer: (data: CreateOfferDto) =>
    api.post<OfferDetail>('/api/offers', data),

  // Update
  updateOffer: (id: string, data: UpdateOfferDto) =>
    api.put<OfferDetail>(`/api/offers/${id}`, data),

  // Status change
  changeOfferStatus: (id: string, newStatus: string, remarks?: string) =>
    api.put<void>(`/api/offers/${id}/status`, { newStatus, remarks }),

  // ─── Documents (Batch 1.7) ───

  uploadDocument: async (offerId: string, file: File, category: string): Promise<OfferDocumentDto> => {
    const token = await api.getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/offers/${offerId}/documents`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const errBody = await response.text();
      throw new Error(errBody || `Upload failed (${response.status})`);
    }
    return response.json();
  },

  downloadDocument: async (offerId: string, documentId: string, fileName: string): Promise<void> => {
    const token = await api.getToken();
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/offers/${offerId}/documents/${documentId}/download`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  deleteDocument: (offerId: string, documentId: string) =>
    api.delete<void>(`/api/offers/${offerId}/documents/${documentId}`),
};
